import React from "react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { CalendarDays } from "lucide-react";

/**
 * ISO YYYY-MM-DD string-based wrapper around shadcn Calendar.
 * Props:
 *   - value: "YYYY-MM-DD" or ""
 *   - onChange: (newIsoString) => void
 *   - min: Date | "YYYY-MM-DD"
 *   - testId: data-testid for the trigger button
 *   - placeholder
 */
export function DatePicker({ value, onChange, min, testId, placeholder = "Pick a date", disabled, unavailableRanges }) {
  const [open, setOpen] = React.useState(false);
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(`${value}T00:00:00`);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);
  const minDate = React.useMemo(() => {
    if (!min) return undefined;
    if (typeof min === "string") return new Date(`${min}T00:00:00`);
    return min;
  }, [min]);
  const isUnavailable = React.useCallback((d) => {
    if (!unavailableRanges || !unavailableRanges.length) return false;
    return unavailableRanges.some((r) => {
      const rs = new Date(`${r.start_date}T00:00:00`);
      const re = new Date(`${r.end_date}T00:00:00`);
      return d >= rs && d < re;
    });
  }, [unavailableRanges]);

  const handle = (d) => {
    if (!d) return;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const label = selectedDate
    ? selectedDate.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={`mt-1 h-10 w-full justify-start text-left font-normal ${!selectedDate ? "text-muted-foreground" : ""}`}
          data-testid={testId}
        >
          <CalendarDays className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handle}
          defaultMonth={selectedDate || minDate || new Date()}
          disabled={(d) => (minDate ? d < minDate : false) || isUnavailable(d)}
          modifiers={{ booked: (d) => isUnavailable(d) }}
          modifiersClassNames={{
            booked: "line-through text-red-400 opacity-60 cursor-not-allowed"
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
